import React, { useCallback, useEffect, useState, useRef } from "react";
import Tooltip from "./Tooltip";
import { playUIClick } from "../Sound";
import { Canvas } from "@react-three/fiber";
import { useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { FaTrash } from "react-icons/fa";
import { DatabaseManager, STORES } from "../managers/DatabaseManager";
import {
    detectGPU,
    getOptimalContextAttributes,
    getRecommendedSettings,
} from "../utils/GPUDetection";
import "../../css/BlockToolsSidebar.css";

const ModelPreview = ({ modelUrl, onRenderComplete }) => {
    const { scene } = useGLTF(modelUrl);
    React.useEffect(() => {
        // Clone the scene so that disposing does not affect cached original
        const clonedScene = scene.clone();

        // Trigger the callback once the model has had a frame to render
        const rafId = requestAnimationFrame(() => {
            if (onRenderComplete) onRenderComplete();
        });

        return () => {
            cancelAnimationFrame(rafId);

            // Clean-up GPU resources
            clonedScene.traverse((object) => {
                if (object.geometry) object.geometry.dispose();
                if (object.material) {
                    if (object.material.map) object.material.map.dispose();
                    object.material.dispose();
                }
            });
        };
    }, [onRenderComplete, scene]);

    const bbox = new THREE.Box3().setFromObject(scene);
    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());

    const maxDim = Math.max(size.x, size.y, size.z);
    const fitScale = 2.1 / maxDim; // Adjust this value to change how much of the canvas the model fills
    return (
        <primitive
            object={scene.clone()}
            scale={fitScale}
            position={[
                -center.x * fitScale,
                -center.y * fitScale,
                -center.z * fitScale,
            ]}
            rotation={[-0.2, -0.5, 0]}
        />
    );
};

const MAX_SIMULTANEOUS_RENDERS = 2; // Allow a couple renders at the same time for faster overall loading
const MAX_RETRIES = 50;
const RETRY_DELAY_MS = 300; // 300-500 ms window per request
let activeRenders = 0;
let renderQueue = [];

// In-memory cache to avoid database lookups and prevent reloading
const imageCache = new Map();

// Clear cache if it gets too large (optional cleanup)
const clearCacheIfNeeded = () => {
    if (imageCache.size > 100) {
        imageCache.clear();
    }
};

const startNextRender = () => {
    setTimeout(() => {
        if (
            renderQueue.length > 0 &&
            activeRenders < MAX_SIMULTANEOUS_RENDERS
        ) {
            const nextRender = renderQueue.shift();
            activeRenders++;
            nextRender();
        }
    }, 100); // 200ms delay between renders
};

const formatModelName = (name) => {
    if (!name) return name;
    return name
        .split("-")
        .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
};

const EnvironmentButton = ({ envType, onSelect, isSelected, onDelete }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [showCanvas, setShowCanvas] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isQueued, setIsQueued] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    const mountedRef = useRef(true);

    // Get GPU-optimized settings for this environment preview
    const gpuInfo = detectGPU();
    const contextAttributes = getOptimalContextAttributes(gpuInfo);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const getCacheKey = useCallback(() => {
        if (envType.modelUrl.startsWith("blob:")) {
            return envType.name;
        }

        return envType.modelUrl.replace(/^\//, "");
    }, [envType.modelUrl, envType.name]);

    useEffect(() => {
        if (!envType) return;

        mountedRef.current = true;

        const loadThumbnail = async () => {
            // OPTIMIZATION: Use pre-generated thumbnail if available
            if (envType.thumbnailUrl) {
                // Construct thumbnail path using PUBLIC_URL (needed for Electron file:// protocol)
                let thumbnailPath;
                if (envType.thumbnailUrl.startsWith("http://") || envType.thumbnailUrl.startsWith("https://") || envType.thumbnailUrl.startsWith("blob:")) {
                    thumbnailPath = envType.thumbnailUrl;
                } else if (envType.thumbnailUrl.startsWith("assets/")) {
                    // Use PUBLIC_URL for proper path resolution in Electron
                    const cleanPath = envType.thumbnailUrl.replace(/^\/+/, "");
                    thumbnailPath = `${process.env.PUBLIC_URL || ""}/${cleanPath}`.replace(/\/+/g, "/").replace(/^\/\//, "/");
                } else {
                    thumbnailPath = envType.thumbnailUrl;
                }

                // Try loading the pre-generated thumbnail
                try {
                    const img = new Image();
                    await new Promise((resolve, reject) => {
                        img.onload = resolve;
                        img.onerror = reject;
                        img.src = thumbnailPath;
                    });

                    if (mountedRef.current) {
                        setImageUrl(thumbnailPath);
                        setIsLoading(false);
                        setRetryCount(0);
                        return true;
                    }
                } catch (error) {
                    console.warn(
                        `[EnvButton] (${envType.name}) Failed to load thumbnail, falling back to cache/render:`,
                        error
                    );
                }
            }

            const cacheKey = getCacheKey();

            // First check in-memory cache
            if (imageCache.has(cacheKey)) {
                const cachedImage = imageCache.get(cacheKey);
                if (mountedRef.current) {
                    setImageUrl(cachedImage);
                    setIsLoading(false);
                    setRetryCount(0);
                    return true;
                }
            }

            // Then check database cache
            try {
                const cachedImage = await DatabaseManager.getData(
                    STORES.PREVIEWS,
                    cacheKey
                );
                if (
                    cachedImage &&
                    cachedImage.startsWith("data:image/") &&
                    mountedRef.current
                ) {
                    // Store in memory cache for future use
                    imageCache.set(cacheKey, cachedImage);
                    setImageUrl(cachedImage);
                    setIsLoading(false);
                    setRetryCount(0);
                    return true;
                }
            } catch (error) {
                console.warn(
                    "Failed to load cached image for:",
                    cacheKey,
                    error
                );
            }
            return false;
        };

        const startRender = () => {
            if (mountedRef.current) {
                setShowCanvas(true);
                setIsQueued(false);
            }
        };

        // Only load/render if we don't have an image already
        if (!imageUrl && !showCanvas && !isQueued && !hasError) {
            setIsLoading(true);
            loadThumbnail().then((hasImage) => {
                if (!mountedRef.current) return; // Check if still mounted

                if (!hasImage) {
                    // Fallback to rendering model if no thumbnail available
                    setIsQueued(true);
                    if (activeRenders < MAX_SIMULTANEOUS_RENDERS) {
                        activeRenders++;
                        startRender();
                    } else {
                        renderQueue.push(startRender);
                    }
                } else {
                    setIsLoading(false);
                }
            });
        }

        return () => {
            mountedRef.current = false; // cleanup flag
            if (isQueued) {
                renderQueue = renderQueue.filter(
                    (render) => render !== startRender
                );
            }
        };
    }, [
        envType,
        getCacheKey,
        // Removed imageUrl, showCanvas, isQueued, hasError, retryCount from dependencies
        // to prevent unnecessary re-runs when these change
    ]);

    if (!envType) {
        return null;
    }

    const handleRenderComplete = () => {
        const canvas = document.querySelector(
            `#preview-${envType.name} canvas`
        );
        if (canvas) {
            try {
                const url = canvas.toDataURL("image/png");
                const cacheKey = getCacheKey();

                // Store in both memory and database cache
                clearCacheIfNeeded();
                imageCache.set(cacheKey, url);
                setImageUrl(url);
                setIsLoading(false);
                setRetryCount(0);

                DatabaseManager.saveData(STORES.PREVIEWS, cacheKey, url).catch(
                    (error) => {
                        // Failure to store the cached image should not trigger a visual retry.
                        // Log it but proceed – the preview itself rendered fine.
                        console.warn(
                            `[EnvButton] (${envType.name}) saveData (cache write) failed:`,
                            error
                        );
                    }
                );
            } catch (error) {
                console.warn(
                    `[EnvButton] (${envType.name}) toDataURL threw on attempt ${
                        retryCount + 1
                    }:`,
                    error
                );

                setIsLoading(false);
                setShowCanvas(false);
                activeRenders = Math.max(0, activeRenders - 1);

                if (retryCount + 1 < MAX_RETRIES) {
                    setTimeout(() => {
                        if (!mountedRef.current) return;
                        setRetryCount((c) => c + 1);
                        setHasError(false);
                        setIsQueued(false);
                    }, RETRY_DELAY_MS);
                } else {
                    console.warn(
                        `[EnvButton] (${envType.name}) Max retries reached. Marking as error.`
                    );
                    setHasError(true);
                    startNextRender();
                }
                return;
            }
            setShowCanvas(false);
            activeRenders--;

            // Proactively release the GL context of the off-screen canvas to keep GPU memory low
            try {
                const gl =
                    canvas.getContext("webgl2") || canvas.getContext("webgl");
                if (gl && gl.getExtension("WEBGL_lose_context")) {
                    gl.getExtension("WEBGL_lose_context").loseContext();
                }
            } catch (glErr) {
                // noop – extension not available
            }

            startNextRender();
        }
    };

    // Handle image loading completion
    const handleImageLoad = () => {
        setIsLoading(false);
    };

    return (
        <Tooltip text={formatModelName(envType.name)}>
            <button
                className={`border transition-all duration-150 environment-button border-white/0 hover:border-white/20 active:border-white`}
                style={{
                    border: isSelected ? "1px solid #ffffff" : "",
                }}
                onClick={() => {
                    onSelect(envType);
                    playUIClick();
                }}
            >
                <div className="object-preview" id={`preview-${envType.name}`}>
                    {showCanvas ? (
                        <Canvas
                            camera={{ fov: 20, position: [0, 0, 8] }}
                            gl={{
                                ...contextAttributes,
                                preserveDrawingBuffer: true, // Override for image capture
                            }}
                        >
                            <ambientLight intensity={1} />
                            <directionalLight
                                position={[5, 5, 5]}
                                intensity={1}
                            />
                            <ModelPreview
                                modelUrl={envType.modelUrl}
                                onRenderComplete={handleRenderComplete}
                            />
                        </Canvas>
                    ) : imageUrl ? (
                        <>
                            <img
                                src={imageUrl}
                                alt={envType.name}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    display: isLoading ? "none" : "block",
                                }}
                                onLoad={handleImageLoad}
                            />
                            {isLoading && (
                                <div className="model-loading-spinner">
                                    <div className="w-5 h-5 rounded-full border-2 animate-spin border-white/30 border-t-white/80"></div>
                                </div>
                            )}
                        </>
                    ) : isLoading ? (
                        <div className="model-loading-spinner">
                            <div className="w-5 h-5 rounded-full border-2 animate-spin border-white/30 border-t-white/80"></div>
                        </div>
                    ) : hasError ? (
                        <div className="flex justify-center items-center model-loading-failed text-white/60">
                            <FaTrash title="Preview unavailable" />
                        </div>
                    ) : (
                        <div className="model-loading-spinner">
                            <div className="w-5 h-5 rounded-full border-2 animate-spin border-white/30 border-t-white/80"></div>
                        </div>
                    )}
                </div>
                <div className="environment-label-wrapper">
                    <div className="environment-button-label text-[#F1F1F1]">
                        {formatModelName(envType.name)}
                    </div>
                </div>
            </button>
        </Tooltip>
    );
};
export default React.memo(EnvironmentButton);
